import React, { useCallback } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import ActivityStatusOption from "@mapmycustomers/shared/enum/activity/ActivityStatusOption";
import { Activity } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import User from "@mapmycustomers/shared/types/User";

import noActivities from "@app/assets/list/preview/no-activities.svg";
import RecapRange from "@app/enum/preview/RecapRange";
import { getActiveActivityTypes } from "@app/store/activity";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";

import NoData from "../NoData";

import ActivityChart from "./ActivityChart";
import styles from "./ActivityPanel.module.scss";
import FilterBar from "./FilterBar";

interface Props {
  activities: Activity[];
  activitiesTotal: number;
  activityTypes: ActivityType[];
  analyticIssuer?: AnalyticsService;
  filter: ActivityStatusOption;
  loading: boolean;
  onChangeActivityTypes: (activityTypes: ActivityType[]) => void;
  onChangeAssignees: (assignees: User["id"][]) => void;
  onChangeRecapRange: (recapRange: RecapRange) => void;
  onFilterChange: (filter: ActivityStatusOption) => void;
  recapRange: RecapRange;
  selectedActivityTypes: ActivityType[];
  selectedAssignees: User["id"][];
  users: User[];
}

const ActivitiesPanel: React.FC<Props> = ({
  activities,
  activitiesTotal,
  activityTypes,
  analyticIssuer,
  filter,
  loading,
  onChangeActivityTypes,
  onChangeAssignees,
  onChangeRecapRange,
  onFilterChange,
  recapRange,
  selectedActivityTypes,
  selectedAssignees,
  users,
}) => {
  const intl = useIntl();
  const handleChangeRecapRange = useCallback(
    (recapRange: RecapRange) => {
      onChangeRecapRange(recapRange);
      analyticIssuer?.clicked(["Change Activity Graph Selected Timeframe", recapRange]);
    },
    [analyticIssuer, onChangeRecapRange]
  );
  const handleChangeActivityTypes = useCallback(
    (activityTypes: ActivityType[]) => {
      onChangeActivityTypes(activityTypes);
      analyticIssuer?.clicked(["Change Activity Graph Selected Types "], { types: activityTypes });
    },
    [analyticIssuer, onChangeActivityTypes]
  );
  const handleChangeAssignees = useCallback(
    (assignees: User["id"][]) => {
      onChangeAssignees(assignees);
      analyticIssuer?.clicked(["Change Activity Graph Selected Assignees "]);
    },
    [analyticIssuer, onChangeAssignees]
  );

  return (
    <>
      {activitiesTotal > 0 ? (
        <div className={styles.container}>
          <FilterBar
            activityTypes={activityTypes}
            filter={filter}
            onChangeActivityTypes={handleChangeActivityTypes}
            onChangeAssignees={handleChangeAssignees}
            onChangeRecapRange={handleChangeRecapRange}
            onFilterChange={onFilterChange}
            recapRange={recapRange}
            selectedActivityTypes={selectedActivityTypes}
            selectedAssignees={selectedAssignees}
            users={users}
          />
          <ActivityChart
            activities={activities}
            activityTypes={selectedActivityTypes}
            loading={loading}
            recapRange={recapRange}
          />
        </div>
      ) : (
        <NoData
          imageSrc={noActivities}
          text={intl.formatMessage({
            id: "preview.activityList.noData",
            defaultMessage: "Add an activity to populate this chart",
            description: "Text of the 'No Data' state in Activities section in a Preview pane",
          })}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypes: getActiveActivityTypes(state),
  users: getUsers(state),
});

export default connect(mapStateToProps)(ActivitiesPanel);
